import type { ReactNode } from 'react';

import { ToastContext, useToastState } from './ToastContext';
import { ToastViewport } from './ToastViewport';
import type { ToastViewportPosition } from './types';

export type ToastProviderProps = {
  children: ReactNode;
  /** Fixed anchor for the toast stack (corners and top or bottom center). @default bottom-right */
  position?: ToastViewportPosition;
  /**
   * If `true`, the viewport renders in the current DOM tree (e.g. SSR or tests).
   * If `false` (default), the viewport is portaled to `document.body` for
   * predictable stacking above modals and the rest of the page.
   */
  renderInPlace?: boolean;
  /**
   * When `true`, each **visible** queued toast uses a focusable accessibility shell before
   * prefix / suffix / dismiss; two or more toasts also get a single stack-level `role="group"`.
   * Does not change pointer hover behavior. Set to `false` to avoid extra tab stops.
   * @default true
   */
  toastShellTabStop?: boolean;
};

/**
 * Provides {@link useToast} for queued, transient toasts. Experimental.
 *
 * Queued toasts: auto-dismiss pauses while the stack is hovered, has keyboard
 * focus-visible inside the viewport, or an exit animation is running; **Escape**
 * dismisses the newest toast only when focus is **inside** the toast viewport (e.g.
 * after **F6** or Tab into a toast), and not when focus is inside an `aria-modal` dialog. The
 * viewport region label is localized via notifications i18n.
 *
 * **F6** (while any toast is visible) moves keyboard focus to the toast viewport
 * landmark (`role="region"`, `tabIndex={-1}`), matching the Base UI Toast pattern;
 * tab from there reaches each toast’s shell and controls when
 * {@link ToastProviderProps.toastShellTabStop} is enabled (default).
 */
export function ToastProvider({
  children,
  position = 'bottom-right',
  renderInPlace = false,
  toastShellTabStop = true,
}: ToastProviderProps) {
  const { value, toasts, dismiss } = useToastState();

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastViewport
        position={position}
        renderInPlace={renderInPlace}
        toastShellTabStop={toastShellTabStop}
        toasts={toasts}
        onDismiss={dismiss}
      />
    </ToastContext.Provider>
  );
}
